import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { LoadingPage } from '@/components/LoadingSpinner';
import { LoginPage } from '@/pages/LoginPage';
import { HomePage } from '@/pages/HomePage';
import { WorkoutsListPage } from '@/pages/WorkoutsListPage';
import { WorkoutFormPage } from '@/pages/WorkoutFormPage';
// recharts-raskaat sivut lazy-latauksena → recharts omaan chunkkiinsa (osio 5.4 / 13.8)
const StatsPage = lazy(() => import('@/pages/StatsPage').then((m) => ({ default: m.StatsPage })));
import { HealthPage } from '@/pages/HealthPage';
const HealthTrendsPage = lazy(() => import('@/pages/HealthTrendsPage').then((m) => ({ default: m.HealthTrendsPage })));
import { WearableSettingsPage } from '@/pages/WearableSettingsPage';
import { ProgramsPage } from '@/pages/ProgramsPage';
import { ProgramDetailPage } from '@/pages/ProgramDetailPage';
import { TemplateFormPage } from '@/pages/TemplateFormPage';
import { StartWorkoutPage } from '@/pages/StartWorkoutPage';
import { LiveWorkoutPage } from '@/pages/LiveWorkoutPage';
import { WorkoutDetailPage } from '@/pages/WorkoutDetailPage';
const WeightPage = lazy(() => import('@/pages/WeightPage').then((m) => ({ default: m.WeightPage })));
import { WeightHistoryPage } from '@/pages/WeightHistoryPage';
import { WeightSettingsPage } from '@/pages/WeightSettingsPage';
import { SquatProgramPage } from '@/pages/SquatProgramPage';
import { SquatSessionPage } from '@/pages/SquatSessionPage';
const SquatStatsPage = lazy(() => import('@/pages/SquatStatsPage').then((m) => ({ default: m.SquatStatsPage })));

// Suojattu reitti -komponentti
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Layout autentikoiduille sivuille
function AuthenticatedLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Skip to content link - visible only on focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-100 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
      >
        Siirry sisältöön
      </a>
      <Header />
      <main
        id="main-content"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 md:pb-6"
        tabIndex={-1}
      >
        {children}
      </main>
      <BottomNav />
    </div>
  );
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Suspense fallback={<LoadingPage />}>
    <Routes>
      {/* Julkiset reitit */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />
        }
      />

      {/* Suojatut reitit */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <HomePage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkoutsListPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts/new"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkoutFormPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts/live/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <LiveWorkoutPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts/:id"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkoutDetailPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/workouts/:id/edit"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WorkoutFormPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/stats"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <StatsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Health routes */}
      <Route
        path="/health"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <HealthPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/health/trends"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <HealthTrendsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/health/settings"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WearableSettingsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Programs routes */}
      <Route
        path="/programs"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ProgramsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/programs/:programId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <ProgramDetailPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/programs/:programId/templates/:templateId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <TemplateFormPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/programs/:programId/templates/:templateId/start"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <StartWorkoutPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Weight tracking routes */}
      <Route
        path="/weight"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WeightPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/weight/history"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WeightHistoryPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/weight/settings"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <WeightSettingsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Kyykkyohjelma */}
      <Route
        path="/kyykkyohjelma"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <SquatProgramPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/kyykkyohjelma/treeni/:sessionId"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <SquatSessionPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/kyykkyohjelma/tilastot"
        element={
          <ProtectedRoute>
            <AuthenticatedLayout>
              <SquatStatsPage />
            </AuthenticatedLayout>
          </ProtectedRoute>
        }
      />

      {/* Dashboard redirect */}
      <Route path="/dashboard" element={<Navigate to="/" replace />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <main className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-gray-300 dark:text-gray-600">404</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">Sivua ei löytynyt</p>
              <a
                href="/"
                className="btn btn-primary mt-4 inline-block focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              >
                Takaisin etusivulle
              </a>
            </div>
          </main>
        }
      />
    </Routes>
    </Suspense>
  );
}
